/** Computed shadows: a sheared silhouette of the thing that casts it.
 *
 * Nothing in the art set ships a shadow, and nothing should: a shadow depends
 * on where the object stands, not on what it is. Without one every sprite looks
 * pasted onto the terrain rather than standing on it - the "sticker" read that
 * is the second most common tell of an amateur 2D map after depth sorting.
 *
 * ## What this replaced, and why
 *
 * The first version was a soft radial ellipse pooled under the object's
 * footprint. Measured on our own adventure screenshot - blob centroid offset
 * from bounding-box centre, normalised, over the 47 shadow blobs of 40px or
 * more - it came out at x = +0.007, y = +0.066: dead symmetric, with a peak
 * darkening of 51/255 (20%) and a 20-25px soft falloff on each flank. That is a
 * CSS box-shadow. It says "this element is elevated"; it does not say "there is
 * a light source over there".
 *
 * The classic says the second thing. This used to be cited to
 * A reference screenshot, WHICH DOES
 * NOT EXIST - no `refs/adventure/` directory has ever been on this machine - and
 * with it went a "signpost pillar" that appears in no reference on disk.
 *
 * Re-measured 2026-08-30 on a reference screenshot sibling a reference screenshot in the
 * critic's reference set (a reference screenshot): a genuine adventure-map
 * frame from the classic gallery for *the classic: Complete*, sand terrain, which is the
 * highest ground/shadow contrast in the set. Two objects there stand on open
 * sand with an unobstructed cast shadow - a stone obelisk (apex y=588, ground
 * y=700, so 112px tall in the frame's own pixels) and a carved idol (top y=685,
 * ground y=773, 88px). Both readings:
 *
 *   direction   LEFT and AWAY from the viewer (up-screen), on both objects and
 *               on every other shadow in the frame, so the light is a single
 *               fixed source off the near right. This part was right.
 *   lean        obelisk (0.45, 0.70), idol (0.28, 0.68), as fractions of the
 *               caster's height. See `LEAN_X` / `LEAN_Y` - the shipped pair was
 *               (0.62, 0.50), which is the same LENGTH and the wrong ANGLE.
 *   strength    shadowed sand against lit sand of the same terrain, median over
 *               a 30x38 patch inside the idol's shadow (1,140 px) versus two
 *               42x38 patches of lit sand either side of it: per channel
 *               (0.501, 0.475, 0.500). The obelisk's narrower slab (18x34)
 *               reads (0.558, 0.568, 0.569). So the darkening is 43-50%, not
 *               the 33% that was claimed here.
 *   blend       the sand's grain reads THROUGH it, and the three channel ratios
 *               agree to within 0.025 (idol) and 0.011 (obelisk): a NEUTRAL
 *               multiply of the destination, not a grey shape painted over it
 *               and not a tinted one.
 *
 * Bias, stated because it only runs one way: `classic7.jpg` is a 2x smooth upscale
 * of a native 800x600 frame, then JPEG. Both operations pull a dark region
 * toward the bright ground around it, so a measured shadow/lit ratio is an
 * UPPER bound on the darkness - the true shadow is at least this dark, never
 * less. That is why the wide idol slab (0.50) is trusted over the narrow
 * obelisk one (0.57): the narrow one has proportionally more edge in it.
 *
 * So: the object's own texture, sheared onto the ground, tinted to nothing, at
 * multiply. Multiply with a black tint gives `dst * (1 - alpha)` inside the
 * opaque pixels, so the terrain's own texture survives inside the shadow.
 *
 * The paragraph that used to stand here said the classic's residual light in shadow
 * "leans red (0.702 red against 0.646 blue)" and that a black tint therefore
 * gives up a little warmth. **That is not what the reference measures.** On the
 * idol the ratios are R 0.501 / G 0.475 / B 0.500 - green lowest, red and blue
 * equal - and on the obelisk R 0.558 / G 0.568 / B 0.569 - red lowest. There is
 * no consistent hue in it; a neutral black multiply is what the original does,
 * which is what this file already did for other reasons.
 */

import type { Texture } from "pixi.js";
import { Container, Sprite } from "pixi.js";
import type { FrameMeta } from "./frames.js";

/**
 * Where the tip of a shadow lands, as a fraction of the caster's height: this
 * far to the left, and this far away from the viewer.
 *
 * `hypot(0.38, 0.70) = 0.797`, so the shadow is about four fifths as long as the
 * object is tall. They are a pair, not two knobs: together they fix the light's
 * direction, and every shadow in the frame has to agree or the scene has two
 * suns.
 *
 * These were (0.62, 0.50), whose length is `hypot = 0.797` - the SAME number to
 * three places. The length was never the error; the angle was. Measured on
 * a reference screenshot (see the header), the two objects with a clean cast shadow give
 * a lean of (0.45, 0.70) and (0.28, 0.68); the pair below is 0.38 for the mean
 * of the two lateral readings and 0.70 for the two vertical ones, then left at
 * the length the shipped pair already had. The shadow therefore swings from 51.1
 * degrees off the away-axis to 28.5 - it lies further behind the object and less
 * beside it - and does not change length by a pixel.
 *
 * The lateral component is the loose one: 0.28 against 0.45 is a real spread and
 * it is not measurement noise, because the classic's object shadows are hand-drawn per
 * sprite rather than projected. 0.38 is the middle of a range, not a constant
 * recovered from the original.
 */
const LEAN_X = 0.38;
const LEAN_Y = 0.70;

/** The light, as a pair of ratios. Exported so a test can pin the sign of each. */
export const SHADOW_LEAN = Object.freeze({ x: LEAN_X, y: LEAN_Y });

/**
 * The shear, as Pixi's transform wants it.
 *
 * Pixi builds `a = cos(skewY)*scaleX`, `b = sin(skewY)*scaleX`,
 * `c = sin(skewX)*scaleY`, `d = cos(skewX)*scaleY` at zero rotation, and applies
 * `x = a*lx + c*ly`, `y = b*lx + d*ly`. We want `a = 1`, `b = 0`, `c = LEAN_X`,
 * `d = LEAN_Y`, so that a local point `ly` above the ground line (`ly` negative)
 * moves left by `LEAN_X*|ly|` and up by `LEAN_Y*|ly|` while the ground row
 * itself does not move at all.
 */
const SHADOW_SKEW_X = Math.atan2(LEAN_X, LEAN_Y);
const SHADOW_SCALE_Y = Math.hypot(LEAN_X, LEAN_Y);

/**
 * Put one sprite into the shape of `req`'s shadow. Shared with the decal layer,
 * which bakes its shadows into the terrain chunk instead of pooling them, so
 * that the scatter and the map objects cannot drift into two light sources.
 */
export function shapeShadow(s: Sprite, req: ShadowRequest): void {
  const f = req.frame;
  if (f.w <= 0 || f.h <= 0) { s.visible = false; return; }
  s.blendMode = "multiply";
  s.tint = 0x000000;
  s.skew.x = SHADOW_SKEW_X;
  // The transform origin is the frame's GROUND POINT, not its corner: that is
  // the one point of the sprite the shear must leave where it is.
  s.anchor.set(f.anchorX / f.w, f.anchorY / f.h);
  s.scale.set(1, SHADOW_SCALE_Y);
  s.position.set(req.groundX, req.groundY);
  s.alpha = Math.max(0, Math.min(1, req.strength));
}

export interface ShadowRequest {
  readonly groundX: number;
  readonly groundY: number;
  readonly frame: FrameMeta;
  /** The caster's own texture. The shadow is its silhouette, not an ellipse. */
  readonly texture: Texture;
  /** 0..1, the fraction of the ground's light the silhouette removes. */
  readonly strength: number;
}

/**
 * A pooled multiply layer. Callers `begin()`, `add()` once per object in the
 * same order they draw them, then `end()`; the pool keeps the sprites so a
 * frame with a stable object set allocates nothing.
 */
export class ShadowLayer {
  readonly container = new Container({ label: "shadows" });
  private readonly pool: Sprite[] = [];
  private used = 0;

  begin(): void { this.used = 0; }

  add(req: ShadowRequest): void {
    if (req.strength <= 0) return;
    if (req.frame.w <= 0 || req.frame.h <= 0) return;
    let s = this.pool[this.used];
    if (s === undefined) {
      s = new Sprite(req.texture);
      s.blendMode = "multiply";
      s.tint = 0x000000;
      s.skew.x = SHADOW_SKEW_X;
      this.pool.push(s);
      this.container.addChild(s);
    } else if (s.texture !== req.texture) {
      s.texture = req.texture;
    }
    s.visible = true;
    shapeShadow(s, req);
    this.used++;
  }

  end(): void {
    for (let i = this.used; i < this.pool.length; i++) this.pool[i]!.visible = false;
  }

  destroy(): void {
    this.container.destroy({ children: true, context: true });
    this.pool.length = 0;
  }
}

/**
 * How much light a thing takes out of the ground, as a fraction.
 *
 * The band is 0.26 to 0.44. It used to be justified by "The classic's shadow body
 * measures a 33% darkening and its deepest part 40-45%", cited to a file that
 * does not exist. The measurement that replaces it (see the header, on
 * a reference screenshot) says **43-50%**, i.e. The original is DARKER than this band's
 * top, and darker still than the measured figure because the upscale-and-JPEG
 * bias only ever lightens a dark region.
 *
 * **The band is deliberately not raised to match.** `test/world-shadows.spec.ts`
 * pins `shadowStrength(frame(64, 512)) <= 0.45`, and moving the cap to 0.50
 * would mean loosening that pin in the same change that justified it. The
 * tighter pin stays; what is recorded here is that the shipped ceiling is about
 * six points shy of the reference, on purpose and not by accident.
 *
 * The ramp itself is ours either way. The classic's DEF shadows are one flat value for
 * every object; a flat pickup here - which in a near-overhead light barely
 * occludes anything - sits below the bottom of the band on purpose. Nothing
 * returns zero: the previous round gave map objects a shadow and left the
 * decoration scatter without one, and the eye sorted the frame into two
 * populations of object because of it.
 */
export function shadowStrength(frame: FrameMeta, tile: number): number {
  const height = frame.h;
  if (height <= tile * 0.35) return 0.26;
  if (height <= tile) return 0.34;
  return Math.min(0.44, 0.34 + (height / tile - 1) * 0.05);
}
